import { Text } from "@hackthenorth/north";
import React, { RefObject, useCallback } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import { GOOSE_GAME_POINTS_NAME } from "src/shared/constants/gooseGames";
import { redactLoreTerms } from "src/shared/utils/loreReveal";

import { medalProgression } from "../modals/hacker/home/quest/constants";

const ProgressionToolTip = ({
  currMilestone,
  progressBackgroundRef,
  progressForegroundRef,
}: {
  currMilestone: number | null;
  progressBackgroundRef: RefObject<HTMLDivElement>;
  progressForegroundRef: RefObject<HTMLDivElement>;
}) => {
  const clampProgressionTooltipPosition = ({
    left,
    top,
    width,
    height,
  }: {
    left: number;
    top: number;
    width: number;
    height: number;
  }) => {
    const PROGRESSION_TOOLTIP_VIEWPORT_PADDING = 16;
    const maxLeft =
      window.innerWidth - width - PROGRESSION_TOOLTIP_VIEWPORT_PADDING;

    return {
      left: Math.max(
        PROGRESSION_TOOLTIP_VIEWPORT_PADDING,
        Math.min(left, maxLeft)
      ),
      top: Math.min(
        Math.max(PROGRESSION_TOOLTIP_VIEWPORT_PADDING, top),
        window.innerHeight - height - PROGRESSION_TOOLTIP_VIEWPORT_PADDING
      ),
    };
  };

  const overrideTooltipPosition = useCallback(
    (
      position: { left: number; top: number },
      _event: Event,
      _target: EventTarget,
      refNode: HTMLDivElement | HTMLSpanElement | null
    ) => {
      const PROGRESSION_TOOLTIP_WIDTH = 350;
      const PROGRESSION_TOOLTIP_HEIGHT = 200;

      const foreground = progressForegroundRef.current;
      const anchor = foreground ?? progressBackgroundRef.current;
      if (!anchor) return position;

      const rect = anchor.getBoundingClientRect();
      const anchorLeft = foreground ? rect.right : rect.left;
      const tooltipWidth = refNode?.offsetWidth ?? PROGRESSION_TOOLTIP_WIDTH;
      const tooltipHeight = refNode?.offsetHeight ?? PROGRESSION_TOOLTIP_HEIGHT;

      return clampProgressionTooltipPosition({
        left: anchorLeft - tooltipWidth / 2,
        top: rect.top - tooltipHeight,
        width: tooltipWidth,
        height: tooltipHeight,
      });
    },
    [progressBackgroundRef, progressForegroundRef]
  );

  const progressionDescription = currMilestone
    ? medalProgression[Number(currMilestone)].description
    : undefined;
  const displayedPointsName = redactLoreTerms(
    GOOSE_GAME_POINTS_NAME,
    new Date()
  );

  return (
    <StyledReactTooltip
      id={"progression-tooltip"}
      backgroundColor="white"
      effect="solid"
      place="top"
      overridePosition={overrideTooltipPosition}
    >
      <DetailsWrapper>
        <Text mods="heading h3">Achievement Progression</Text>
        <StyledSubDescriptionText mods="p">
          Progress tracked based on your all-time balance.
        </StyledSubDescriptionText>
        <br />
        <StyledDescriptionText mods="p">
          {progressionDescription
            ? progressionDescription
            : `Collect 50 ${displayedPointsName} to earn your first medal!`}
        </StyledDescriptionText>
      </DetailsWrapper>
    </StyledReactTooltip>
  );
};

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
  overflow: hidden;
  width: 300px;
`;

const StyledSubDescriptionText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary} !important;
  white-space: pre-line;
`;

const StyledDescriptionText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary} !important;
  font-weight: 700;
  white-space: pre-line;
`;

const StyledReactTooltip = styled(ReactTooltip)<{ eventDelay?: number }>`
  border-radius: 10px !important;
  padding: 24px 32px 24px 24px !important;

  opacity: 0 !important;
  transition: opacity 0.3s ease-out, visibility 0.3s ease-out !important;
  color: ${({ theme }) => theme.globalConstants.color.white} !important;
  box-shadow: 0px 10px 15px -3px #0000001e;

  &.__react_component_tooltip.show {
    opacity: 1 !important;
  }
`;

export default ProgressionToolTip;
